import type { LabelCheckQuestion } from '~/domain/report/model/report/node/questionNode/labelCheckQuestionNode/labelCheckQuestion'
import type { LabelCheckQuestionNode } from '~/domain/report/model/report/node/questionNode/labelCheckQuestionNode/labelCheckQuestionNode'
import type { QuestionNode } from '~/domain/report/model/report/node/questionNode/questionNode'
import { ApiToModelError } from '../error'
import { selectSiblingNodeIds } from './selectSiblingNodeIds'
import type { ConversionContext } from './types'

/**
 * ラベル検査質問の API Schema を Store のデータ型に変換する
 *
 * @param context
 * @returns
 */
export const convertLabelCheckQuestionNode = (
  context: ConversionContext,
): QuestionNode => {
  const {
    base: { questionNodeBase, questionBase, deviateProperty, node, recordedAt },
  } = context

  const responseLabelCheck = node.question?.responseLabelChecks?.[0]

  /** 事前にラベル検査が指定されている場合は、レポート作成者はラベル検査を選択できない */
  const canSelectLabelInspection = responseLabelCheck
    ? !responseLabelCheck.labelInspectionId
    : true

  const baseTimeReferenceQuestion = createBaseTimeReferenceQuestion(context)
  const referenceTextQuestion = createReferenceTextQuestion(context)

  const labelCheckQuestion: LabelCheckQuestion = {
    ...questionBase,
    ...deviateProperty,
    type: 'labelCheck',
    labelInspection: responseLabelCheck?.labelInspectionId
      ? {
          id: responseLabelCheck.labelInspectionId,
          // ラベル数は永続化されていないため、APIから取得できない。
          // Lobster モジュールの中から副作用で更新する。
          numberOfLabels: 0,
        }
      : undefined,
    canSelectLabelInspection,
    baseTimeReferenceQuestion,
    referenceTextQuestion,
  }

  const labelCheckValue = node.question?.responseAnswer?.labelCheckValue

  const labelCheckAnswer: LabelCheckQuestionNode['answer'] =
    labelCheckValue && {
      type: 'labelCheck',
      recordedAt,
      isInvalid: labelCheckValue.isInvalid === 1,
      reportLabelInspectionId: labelCheckValue.reportLabelInspectionId,
      numberOfLabels: labelCheckValue.numberOfLabels ?? 0,
    }
  const questionNode: LabelCheckQuestionNode = {
    ...questionNodeBase,
    questionType: 'labelCheck',
    question: labelCheckQuestion,
    answer: labelCheckAnswer,
  }

  return questionNode
}

/**
 * UUIDから兄弟ノード内のNodeを探す共通関数
 */
const findReferenceNodeByUuid = (context: ConversionContext, uuid: string) => {
  const {
    wholeContext: { nodeDict },
    base: { node },
  } = context

  const siblingNodeIds = selectSiblingNodeIds(context)

  for (const siblingNodeId of siblingNodeIds) {
    const siblingNode = nodeDict[siblingNodeId]
    if (siblingNode?.uuid === uuid) {
      return siblingNode
    }
  }

  throw new ApiToModelError(
    `Reference node with UUID "${uuid}" is not found in sibling nodes`,
    { node, uuidToFind: uuid, siblingNodeIds },
  )
}

/**
 * 参照質問オブジェクトの作成
 *
 * ラベル検査質問は、他の質問の回答を参照して検査に利用できる。
 * 参照している UUID から取得した質問のタイプはここでは確認していないが、
 * ひな形作成時に正しいタイプの質問が設定されていることを期待する。
 */

// 基準日時として参照する日時質問の作成
const createBaseTimeReferenceQuestion = (context: ConversionContext) => {
  const uuid =
    context.base.node.question?.responseLabelChecks?.[0]
      ?.baseTimeReferenceDatetimeQuestionNodeUUID
  if (!uuid) return

  const referencedNode = findReferenceNodeByUuid(context, uuid)

  return {
    uuid,
    nodeId: referencedNode.id,
    name: referencedNode.question?.name ?? '',
  }
}

// 参照先として指定されたテキスト質問の作成
const createReferenceTextQuestion = (context: ConversionContext) => {
  const uuid =
    context.base.node.question?.responseLabelChecks?.[0]
      ?.referenceTextQuestionNodeUUID
  if (!uuid) return

  const referencedNode = findReferenceNodeByUuid(context, uuid)

  return {
    uuid,
    nodeId: referencedNode.id,
    name: referencedNode.question?.name ?? '',
  }
}
